import { $t } from '@/i18n';
import { AIPageFileChangeType, } from '@/components/version/aiPageDiffDataSource';
const CANCEL_TIMEOUT_MS = 10_000;
const CONNECT_TIMEOUT_MS = 30_000;
const INITIALIZE_TIMEOUT_MS = 180_000;
const MAX_DIFF_FILES = 500;
const OID_PATTERN = /^[0-9a-f]{40}$/i;
function asRecord(value) {
    return typeof value === 'object' && value !== null
        ? value
        : null;
}
function hasErrorCode(error, code) {
    return typeof error === 'object'
        && error !== null
        && error.code === code;
}
function isActiveMergeLoadError(error) {
    if (!hasErrorCode(error, 409))
        return false;
    const data = asRecord(asRecord(error)?.data);
    return data?.operation === 'loadProject'
        && data.reason === 'low_code_merge_active';
}
function assertDiffComplete(diff) {
    if (diff.truncated === true) {
        throw new Error($t('k_8e42464e'));
    }
}
function preparedResult(value) {
    const raw = asRecord(value);
    const diff = asRecord(raw?.diff);
    if (typeof raw?.tree !== 'string'
        || !diff
        || typeof diff.baseCommit !== 'string'
        || !Array.isArray(diff.files)) {
        throw new Error($t('k_e25a81e8'));
    }
    return value;
}
async function initializeRemoteMergeRuntime(options) {
    const abortError = () => Object.assign(new Error($t('k_7da916ad')), { name: 'AbortError' });
    if (options.signal?.aborted)
        throw abortError();
    let runtime;
    let initializationError;
    let rejectInterrupted;
    const controller = new AbortController();
    const interrupted = new Promise((_resolve, reject) => {
        rejectInterrupted = reject;
    });
    const interrupt = (error) => {
        initializationError = error;
        rejectInterrupted(error);
        controller.abort();
    };
    const onAbort = () => interrupt(abortError());
    options.signal?.addEventListener('abort', onAbort, { once: true });
    async function waitForInitialization(operation, timeoutMs = INITIALIZE_TIMEOUT_MS) {
        if (initializationError)
            throw initializationError;
        const timeout = setTimeout(() => interrupt(new Error(timeoutMs === CONNECT_TIMEOUT_MS
            ? $t('k_805f6a3c')
            : $t('k_13804d13'))), timeoutMs);
        try {
            const result = await Promise.race([operation(), interrupted]);
            if (initializationError)
                throw initializationError;
            return result;
        }
        finally {
            clearTimeout(timeout);
        }
    }
    try {
        const { loadGogoLib } = await waitForInitialization(() => import('@/modules/ai-page/runtime-loader'));
        const editorRemote = await waitForInitialization(() => loadGogoLib('editorRemote'));
        if (initializationError)
            throw initializationError;
        runtime = editorRemote.createBrowserHubRuntime({ defaultWsUrl: options.wsUrl });
        const createdRuntime = runtime;
        await waitForInitialization(() => createdRuntime.connect(options.wsUrl), CONNECT_TIMEOUT_MS);
        const workspace = runtime.service('workspace');
        const fs = runtime.service('fs');
        const snapshotService = runtime.service('snapshot');
        try {
            await waitForInitialization(() => workspace.call('loadProject', {
                framework: options.projectFramework,
            }, { signal: controller.signal }));
        }
        catch (error) {
            // Keep this session available so begin can offer an explicit takeover.
            if (!isActiveMergeLoadError(error))
                throw error;
        }
        if (initializationError)
            throw initializationError;
        return { runtime, workspace, fs, snapshotService };
    }
    catch (error) {
        controller.abort();
        runtime?.dispose();
        throw initializationError || error;
    }
    finally {
        // The caller still needs the live session to cancel an active merge on close.
        options.signal?.removeEventListener('abort', onAbort);
    }
}
export async function createRemoteMergeSession(options) {
    const { runtime, workspace, fs, snapshotService } = await initializeRemoteMergeRuntime(options);
    const fileDocuments = new Map();
    let currentPrepared = null;
    let queuedPrepared = null;
    let preparing = false;
    let currentBaseRef = '';
    let disposed = false;
    let filesChangeTimer = null;
    const cancelWaiters = new Set();
    const emitFilesChange = () => {
        if (!currentPrepared)
            return;
        currentPrepared = null;
        if (filesChangeTimer)
            clearTimeout(filesChangeTimer);
        filesChangeTimer = setTimeout(options.onFilesChange, 300);
    };
    const publishPrepared = (result) => {
        if (filesChangeTimer) {
            clearTimeout(filesChangeTimer);
            filesChangeTimer = null;
        }
        currentPrepared = result;
        options.onPrepared(currentPrepared);
    };
    const handlePrepared = (payload) => {
        const result = preparedResult(payload);
        if (preparing) {
            queuedPrepared = result;
            return;
        }
        publishPrepared(result);
    };
    const handleCancelled = () => {
        currentPrepared = null;
        cancelWaiters.forEach(resolve => resolve());
        cancelWaiters.clear();
        options.onCancelled?.();
    };
    const handleFsChange = () => emitFilesChange();
    workspace.on('lowCodeMergePrepared', handlePrepared);
    workspace.on('lowCodeMergeCancelled', handleCancelled);
    fs.on('fsChange', handleFsChange);
    const snapshot = {
        async readFile(params) {
            return await snapshotService.call('readFile', params);
        },
        async workingDiff() {
            const [diffResult, statusResult] = await Promise.all([
                snapshotService.call('diffWorktree', { baseRef: currentBaseRef || 'HEAD', maxFiles: MAX_DIFF_FILES }),
                snapshotService.call('status', { includeEntries: true, includeWorktreeSummary: true, maxFiles: MAX_DIFF_FILES }),
            ]);
            const diff = asRecord(diffResult);
            const status = asRecord(statusResult);
            if (typeof diff?.baseCommit !== 'string' || !Array.isArray(diff.files) || !Array.isArray(status?.entries)) {
                throw new Error($t('k_302958d8'));
            }
            assertDiffComplete(diff);
            const summary = asRecord(status.worktreeSummary);
            if (summary)
                assertDiffComplete(summary);
            const files = new Map(diff.files.map(file => [file.path, file]));
            // Git diff against a commit does not identify unmerged index entries, and
            // may omit conflicts whose worktree content already matches the base.
            for (const entry of status.entries) {
                if (entry.status === 'unmerged') {
                    files.set(entry.path, { ...files.get(entry.path), path: entry.path, status: entry.status });
                }
            }
            // The summary expands untracked directories to file paths; none of its
            // tracked-file statuses are valid against the requested target base.
            // summary 可能只保留暂存删除，仍需读取 entries 中同路径的 untracked 状态。
            const worktreeFiles = (summary?.files || []);
            for (const entry of [...status.entries, ...worktreeFiles]) {
                if (entry.status !== 'untracked' || entry.path.endsWith('/'))
                    continue;
                const existing = files.get(entry.path);
                if (existing?.status === 'deleted') {
                    // 恢复但未暂存的文件在目标版本中已存在，应展示目标原文和当前内容。
                    files.set(entry.path, { path: entry.path, status: 'modified' });
                }
                else if (!existing) {
                    files.set(entry.path, entry);
                }
            }
            assertDiffComplete({ truncated: files.size > MAX_DIFF_FILES });
            return {
                baseCommit: diff.baseCommit,
                files: [...files.values()],
            };
        },
    };
    async function readWorkspaceFile(path) {
        const cached = fileDocuments.get(path);
        if (cached)
            return cached.content.toString();
        const handle = await fs.openDocument('file', { path });
        try {
            await Promise.all([handle.whenOpened(), handle.whenSynced()]);
        }
        catch (error) {
            handle.close();
            throw error;
        }
        if (disposed) {
            handle.close();
            throw new Error($t('k_7da916ad'));
        }
        const content = handle.ydoc.getText('content');
        const observer = () => emitFilesChange();
        content.observe(observer);
        fileDocuments.set(path, { handle, content, observer });
        return content.toString();
    }
    async function writeWorkspaceFile(path, value) {
        const currentValue = await readWorkspaceFile(path);
        const document = fileDocuments.get(path)?.content;
        if (!document || currentValue === value)
            return;
        let start = 0;
        let currentEnd = currentValue.length;
        let nextEnd = value.length;
        while (start < currentEnd && currentValue[start] === value[start])
            start += 1;
        while (currentEnd > start
            && nextEnd > start
            && currentValue[currentEnd - 1] === value[nextEnd - 1]) {
            currentEnd -= 1;
            nextEnd -= 1;
        }
        if (currentEnd > start)
            document.delete(start, currentEnd - start);
        if (nextEnd > start)
            document.insert(start, value.slice(start, nextEnd));
    }
    function disposeSession() {
        if (disposed)
            return;
        disposed = true;
        if (filesChangeTimer)
            clearTimeout(filesChangeTimer);
        queuedPrepared = null;
        cancelWaiters.clear();
        workspace.off('lowCodeMergePrepared', handlePrepared);
        workspace.off('lowCodeMergeCancelled', handleCancelled);
        fs.off('fsChange', handleFsChange);
        fileDocuments.forEach(({ handle, content, observer }) => {
            content.unobserve(observer);
            handle.close();
        });
        fileDocuments.clear();
        runtime.dispose();
    }
    return {
        wsUrl: runtime.connectionUrl.value,
        snapshot,
        async begin(mergeRef, baseRef) {
            const result = asRecord(await workspace.call('beginLowCodeMerge', { ref: mergeRef, baseRef }));
            const status = result?.status;
            if (status !== 'ready' && status !== 'conflicted' && status !== 'up_to_date') {
                throw new Error($t('k_5aaac01a'));
            }
            currentBaseRef = baseRef;
            return status;
        },
        async prepare() {
            preparing = true;
            queuedPrepared = null;
            try {
                const result = asRecord(await workspace.call('saveProject', {
                    message: $t('k_4f7d37a6'),
                    rebuild: true,
                }));
                if (result?.status !== 'completed') {
                    throw new Error($t('k_e424d7ae'));
                }
                if (queuedPrepared)
                    publishPrepared(queuedPrepared);
            }
            finally {
                preparing = false;
                queuedPrepared = null;
            }
        },
        async startSmartMerge() {
            const result = asRecord(await workspace.call('startSmartMerge', {}));
            const conversationId = result?.conversationId;
            if (typeof conversationId !== 'string' || !conversationId) {
                throw new Error($t('k_d63a8600'));
            }
            return conversationId;
        },
        async commit(expectedTree, message) {
            try {
                const result = asRecord(await workspace.call('commitLowCodeMerge', {
                    expectedTree,
                    message,
                }));
                if (typeof result?.head !== 'string' || !OID_PATTERN.test(result.head)) {
                    throw new Error($t('k_67a10bfb'));
                }
                if (typeof result.tree !== 'string'
                    || result.tree.toLowerCase() !== expectedTree.toLowerCase()) {
                    throw Object.assign(new Error($t('k_54a6573b')), {
                        code: 'RESULT_CHANGED',
                    });
                }
                return result.head;
            }
            catch (error) {
                if (hasErrorCode(error, 'RESULT_CHANGED'))
                    emitFilesChange();
                throw error;
            }
        },
        async finish(keepResult) {
            await workspace.call('finishLowCodeMerge', { keepResult });
        },
        async cancel(force = false) {
            let resolveByEvent;
            let timeout;
            const cancelledEvent = new Promise((resolve) => {
                resolveByEvent = resolve;
                cancelWaiters.add(resolve);
            });
            try {
                const completed = await Promise.race([
                    workspace.call('cancelLowCodeMerge', force ? { force: true } : {}).then(() => true),
                    cancelledEvent.then(() => true),
                    new Promise((resolve) => {
                        timeout = setTimeout(() => resolve(false), CANCEL_TIMEOUT_MS);
                    }),
                ]);
                if (!completed)
                    disposeSession();
            }
            finally {
                if (timeout)
                    clearTimeout(timeout);
                cancelWaiters.delete(resolveByEvent);
            }
        },
        getPreparedResult() {
            return currentPrepared;
        },
        getDiffFiles() {
            if (!currentPrepared) {
                throw new Error($t('k_37b333c4'));
            }
            return currentPrepared.diff.files;
        },
        readWorkspaceFile,
        writeWorkspaceFile,
        dispose: disposeSession,
    };
}
function fileName(path) {
    return path.split('/').pop() || path;
}
function toFileChange(entry) {
    const common = {
        path: entry.path,
        name: fileName(entry.path),
        contentLoaded: true,
    };
    if (entry.status === 'added' || entry.status === 'untracked' || entry.status === 'copied') {
        return { ...common, type: AIPageFileChangeType.Added };
    }
    if (entry.status === 'deleted') {
        return { ...common, type: AIPageFileChangeType.Deleted };
    }
    if (entry.status === 'renamed') {
        return {
            ...common,
            type: AIPageFileChangeType.Renamed,
            ...(entry.origPath || entry.oldPath ? { oldPath: entry.origPath || entry.oldPath } : {}),
        };
    }
    return { ...common, type: AIPageFileChangeType.Modified };
}
export function createRemoteMergeAIPageDiffDataSource(session) {
    return {
        allowMissingCompareCommit: true,
        async loadChanges() {
            const prepared = session.getPreparedResult();
            if (prepared)
                assertDiffComplete(prepared.diff);
            const diff = prepared
                ? { baseCommit: prepared.diff.baseCommit, files: session.getDiffFiles() }
                : await session.snapshot.workingDiff();
            const changes = await Promise.all(diff.files.map(async (entry) => {
                const change = toFileChange(entry);
                try {
                    if (change.type !== AIPageFileChangeType.Added) {
                        try {
                            const original = await session.snapshot.readFile({
                                path: change.oldPath || change.path,
                                ref: diff.baseCommit,
                            });
                            change.originalContent = original.content;
                        }
                        catch (error) {
                            if (entry.status === 'unmerged' && hasErrorCode(error, 404)) {
                                change.originalContent = '';
                            }
                            else {
                                throw error;
                            }
                        }
                    }
                    if (change.type !== AIPageFileChangeType.Deleted) {
                        try {
                            change.modifiedContent = await session.readWorkspaceFile(change.path);
                        }
                        catch (error) {
                            if (entry.status !== 'unmerged' || !hasErrorCode(error, 404))
                                throw error;
                            change.modifiedContent = '';
                        }
                    }
                    return change;
                }
                catch (error) {
                    const reason = error instanceof Error ? error.message : String(error);
                    throw new Error($t('k_cacaddea', {
                        arg_0: change.path,
                        arg_1: reason,
                    }));
                }
            }));
            return {
                changes,
                originFiles: [],
                compareFiles: [],
            };
        },
        async loadContent(change) {
            return {
                originalContent: change.originalContent,
                modifiedContent: change.modifiedContent,
            };
        },
        async writeContent(change, content) {
            await session.writeWorkspaceFile(change.path, content);
        },
    };
}
